import { useState, useRef, useEffect } from "react";
import Layout from "@/components/Layout";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import "@/styles/mobileTools.css";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { FaFileContract, FaChartLine, FaLightbulb, FaSearch } from "react-icons/fa";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { FaSearchDollar, FaNetworkWired, FaGlobe, FaImage, FaFileAlt, FaTools, FaCheckCircle, FaCopy } from "react-icons/fa";
import { analyzeContent } from "@/lib/api";
import { AnalysisResult } from "@/lib/api";
import NetworkGraph from "@/components/NetworkGraph";
import { fetchWhoisData } from "@/services/domainService";
import * as exifr from 'exifr';
import { analyzeImageWithAI } from '@/services/geolocationService';
import { analyzeImageWithAI as analyzeImageVerificationAI } from '@/services/imageVerificationService';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
// @ts-ignore
pdfjsLib.GlobalWorkerOptions.workerSrc = "//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";
import mammoth from 'mammoth';
import JSZip from 'jszip';
import { analyzeDocumentWithAI } from '@/services/documentAnalysisService';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MobileToolSlider from "@/components/MobileToolSlider";

const osintCategories = [
  {
    id: "search",
    name: "Advanced Search",
    description: "Tools for specialized search queries and domain investigations",
    icon: <FaSearchDollar className="h-5 w-5" />,
  },
  {
    id: "network",
    name: "Network Analysis",
    description: "Tools to analyze connections and information spread",
    icon: <FaNetworkWired className="h-5 w-5" />,
  },
  {
    id: "geolocation",
    name: "Geolocation",
    description: "Tools to verify and analyze location information",
    icon: <FaGlobe className="h-5 w-5" />,
  },
  {
    id: "image",
    name: "Image Analysis",
    description: "Tools for reverse image search and image verification",
    icon: <FaImage className="h-5 w-5" />,
  },
  {
    id: "document",
    name: "Document Analysis",
    description: "Tools for analyzing PDFs and other documents",
    icon: <FaFileAlt className="h-5 w-5" />,
  },
];

const Tools = () => {
  // Detect if viewing on a mobile device
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [activeTab, setActiveTab] = useState("search");
  const [inputText, setInputText] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeResult, setAnalyzeResult] = useState<AnalysisResult | null>(null);
  const { toast } = useToast();
  
  // Search Operator Builder state
  const [searchPlatform, setSearchPlatform] = useState("Google");
  const [searchType, setSearchType] = useState("Basic Search");
  const [searchKeywords, setSearchKeywords] = useState("");
  const [generatedQuery, setGeneratedQuery] = useState("");
  
  // Network analysis state
  const [networkUrl, setNetworkUrl] = useState("");
  const [networkPlatform, setNetworkPlatform] = useState("All Platforms");
  const [networkTimeRange, setNetworkTimeRange] = useState("Last 24 hours");
  const [networkData, setNetworkData] = useState<any>(null);
  const [isNetworkLoading, setIsNetworkLoading] = useState(false);

  // Domain analysis state
  const [domain, setDomain] = useState("");
  const [whoisResult, setWhoisResult] = useState<any>(null);
  const [whoisLoading, setWhoisLoading] = useState(false);
  const [whoisError, setWhoisError] = useState("");
  const [showRawWhois, setShowRawWhois] = useState(false);

  // Source Analysis History state
  const [sourceHistory, setSourceHistory] = useState<{
    type: string; // e.g., 'domain', 'source'
    input: string; // The query or input
    timestamp: number; // For sorting and display
    result: any; // The analysis result (WHOIS data, AI analysis, etc.)
  }[]>([]);

  // Geolocation analysis state
  const [geoResult, setGeoResult] = useState<any>(null);
  const [geoError, setGeoError] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoFile, setGeoFile] = useState<File | null>(null);
  const [showGeoModal, setShowGeoModal] = useState(false);
  const [geoPreview, setGeoPreview] = useState<string | null>(null);

  // Image Verification Suite state
  const [imgFile, setImgFile] = useState<File | null>(null);
  const [imgPreview, setImgPreview] = useState<string | null>(null);
  const [exifData, setExifData] = useState<any>(null);
  const [manipResult, setManipResult] = useState<any>(null);
  const [aiGenResult, setAiGenResult] = useState<any>(null);
  const [imgError, setImgError] = useState('');
  const [imgLoading, setImgLoading] = useState(false);
  const [exifChecked, setExifChecked] = useState(true);
  const [manipChecked, setManipChecked] = useState(false);
  const [aiGenChecked, setAiGenChecked] = useState(false);

  // Document analysis state
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docMeta, setDocMeta] = useState<any>(null);
  const [aiDocResult, setAiDocResult] = useState<AnalysisResult | null>(null);
  const [docError, setDocError] = useState('');
  const [docLoading, setDocLoading] = useState(false);

  const MAX_FILE_SIZE_MB = 5;

  const platforms = [
    { value: "Google", label: "Google" },
    { value: "Twitter", label: "Twitter" },
    { value: "LinkedIn", label: "LinkedIn" },
    { value: "Facebook", label: "Facebook" },
    { value: "GitHub", label: "GitHub" },
  ];

  const searchOperators: { 
    value: string; 
    label: string; 
    requiresParameter?: boolean; 
    parameterLabel?: string; 
    parameterPlaceholder?: string; 
    platform?: string[]; // Optional: restrict to specific platforms
  }[] = [
    { value: "keywords_only", label: "Keywords Only" },
    { value: "exact_phrase", label: "Exact Phrase" },
    { 
      value: "site_specific", 
      label: "Site Specific", 
      requiresParameter: true, 
      parameterLabel: "Website URL", 
      parameterPlaceholder: "example.com"
    },
    { 
      value: "filetype", 
      label: "Filetype", 
      requiresParameter: true, 
      parameterLabel: "File Extension", 
      parameterPlaceholder: "pdf"
    },
    { value: "exclude_term", label: "Exclude Term", requiresParameter: true, parameterLabel: "Term to Exclude", parameterPlaceholder: "news" },
    { value: "hashtag", label: "Hashtag", platform: ["Twitter", "Facebook", "LinkedIn"] }, // Example platform-specific
    { value: "user_profile", label: "User Profile", platform: ["Twitter", "GitHub"], requiresParameter: true, parameterLabel: "Username", parameterPlaceholder: "username" },
  ];

  // Effect to generate query
  useEffect(() => {
    let query = "";
    const keywords = searchKeywords.trim();
    const parameter = searchType.trim();

    if (!keywords && ! (searchOperators.find(op => op.value === searchType)?.requiresParameter && parameter)) {
      setGeneratedQuery("");
      return;
    }

    switch (searchType) {
      case "keywords_only":
        query = keywords;
        break;
      case "exact_phrase":
        query = `\"${keywords}\"`;
        break;
      case "site_specific":
        if (parameter) query = `site:${parameter} ${keywords ? `\"${keywords}\"` : ''}`.trim();
        else query = keywords ? `\"${keywords}\"` : "";
        break;
      case "filetype":
        if (parameter) query = `${keywords ? `\"${keywords}\" ` : ''}filetype:${parameter}`.trim();
        else query = keywords ? `\"${keywords}\"` : "";
        break;
      case "exclude_term":
        if (parameter) query = `${keywords ? `\"${keywords}\" ` : ''}-${parameter}`.trim();
        else query = keywords ? `\"${keywords}\"` : "";
        break;
      case "hashtag":
        query = `#${keywords.replace(/\s+/g, '')}`;
        break;
      case "user_profile":
        if(searchPlatform === "Twitter") query = `from:${parameter} ${keywords ? `(${keywords})` : ''}`.trim();
        else if(searchPlatform === "GitHub") query = `user:${parameter} ${keywords}`.trim();
        else query = keywords; // Fallback if platform not handled
        break;
      default:
        query = keywords;
    }
    setGeneratedQuery(query);
  }, [searchPlatform, searchType, searchKeywords, searchType]);

  const handleCopyQuery = () => {
    if (generatedQuery) {
      navigator.clipboard.writeText(generatedQuery)
        .then(() => {
          toast({ title: "Success", description: "Query copied to clipboard!" });
        })
        .catch(err => {
          console.error("Failed to copy query: ", err);
          toast({ variant: "destructive", title: "Error", description: "Failed to copy query." });
        });
    }
  };

  const handleTextAnalysis = async () => {
    if (!inputText.trim()) {
      toast({
        title: "Error",
        description: "Please enter some text or URL to analyze.",
        variant: "destructive",
      });
      return;
    }
    setIsAnalyzing(true);
    setAnalyzeResult(null);

    try {
      const result = await analyzeContent({ text: inputText });
      setAnalyzeResult(result);
    } catch (error: any) {
      console.error("Error analyzing text:", error);
      toast({
        title: "Analysis Error",
        description: error.message || "Failed to analyze text. Please try again.",
        variant: "destructive",
      });
      // Set a properly formatted error result
      setAnalyzeResult({
        explanation: `Error during analysis: ${error.message || "An unknown error occurred."}`,
        type: "Error",
        confidence: 0,
        suggestions: ["Please try again with different text", "Check your internet connection", "Verify the text format"],
        sourceAnalysis: {
          credibility: 0,
          summary: "Analysis could not be completed due to an error.",
          factors: []
        }
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleNetworkAnalysis = async () => {
    if (!networkUrl.trim()) {
      toast({
        title: "Error",
        description: "Please enter a URL or keywords to track",
        variant: "destructive",
      });
      return;
    }

    setIsNetworkLoading(true);
    setNetworkData(null);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const mockData = {
        nodes: [
          { id: 'source', label: 'Source', group: 1, size: 25 },
          { id: 'twitter1', label: 'Twitter User 1', group: 2, size: 15 },
          { id: 'twitter2', label: 'Twitter User 2', group: 2, size: 12 },
          { id: 'facebook1', label: 'Facebook Group', group: 3, size: 20 },
          { id: 'reddit1', label: 'Reddit r/news', group: 4, size: 18 },
          { id: 'reddit2', label: 'Reddit r/worldnews', group: 4, size: 14 },
          { id: 'news1', label: 'News Site 1', group: 5, size: 22 },
          { id: 'news2', label: 'News Site 2', group: 5, size: 16 },
          { id: 'blog1', label: 'Blog Post', group: 6, size: 10 },
        ],
        links: [
          { source: 'source', target: 'twitter1', value: 5 },
          { source: 'source', target: 'facebook1', value: 8 },
          { source: 'twitter1', target: 'twitter2', value: 3 },
          { source: 'facebook1', target: 'reddit1', value: 5 },
          { source: 'reddit1', target: 'reddit2', value: 2 },
          { source: 'source', target: 'news1', value: 7 },
          { source: 'news1', target: 'news2', value: 4 },
          { source: 'news2', target: 'blog1', value: 2 },
          { source: 'twitter2', target: 'blog1', value: 1 },
        ]
      };
      
      setNetworkData(mockData);
      
      toast({
        title: "Analysis Complete",
        description: "Network spread analysis has been completed",
      });
    } catch (error) {
      toast({
        title: "Analysis Error",
        description: "Failed to analyze the network spread. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsNetworkLoading(false);
    }
  };

  const handleDomainAnalyze = async () => {
    setWhoisLoading(true);
    setWhoisError("");
    setWhoisResult(null);
    try {
      const data = await fetchWhoisData(domain);
      setWhoisResult(data);
      // Add to history
      setSourceHistory(prevHistory => [
        { type: 'domain', input: domain, timestamp: Date.now(), result: data },
        ...prevHistory,
      ]);
    } catch (err) {
      setWhoisError("Failed to fetch WHOIS data. Please check the domain and try again.");
    }
    setWhoisLoading(false);
  };

  const handleClearSourceHistory = () => {
    setSourceHistory([]);
    toast({
      title: "History Cleared",
      description: "Source analysis history has been cleared.",
    });
  };

  const handleGeoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGeoError('');
    setGeoResult(null);
    setGeoFile(null);
    setGeoPreview(null);
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setGeoPreview(previewUrl);
    setGeoFile(file);
  };

  const handleVerifyLocation = async () => {
    setGeoError('');
    setGeoResult(null);
    setGeoLoading(true);
    if (!geoFile) { setGeoLoading(false); return; }
    if (!geoFile.type.startsWith('image/')) {
      setGeoError('Unsupported file type. Please upload an image.');
      setGeoLoading(false);
      return;
    }
    if (geoFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setGeoError('File is too large. Please upload an image smaller than 5MB.');
      setGeoLoading(false);
      return;
    }
    try {
      const exif = await exifr.gps(geoFile);
      let gps = null;
      if (exif && exif.latitude && exif.longitude) {
        gps = { latitude: exif.latitude, longitude: exif.longitude };
      }

      // Convert image to base64 and analyze with AI
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (e.target?.result) {
          const base64Image = e.target.result.toString();

          // Create a specific prompt for geolocation analysis
          let customPrompt = `Analyze this image to determine the location where it was taken.`;
          if (gps) {
            customPrompt += ` I have also extracted GPS coordinates from the image metadata: Latitude ${gps.latitude}, Longitude ${gps.longitude}. Use these coordinates to help verify and pinpoint the location depicted in the image.`;
          }
          customPrompt += `\n\nProvide a detailed analysis including:\n1. The identified location (city, country, landmarks, etc.)\n2. How you identified the location (visual cues, matching to satellite/street view, using GPS data)\n3. Your confidence level in the identification (0-100%).\n4. Suggestions for further verification.\n\nFormat your response as JSON with the following structure:\n{\n  \"explanation\": \"detailed explanation of the location and how it was identified\",\n  \"type\": \"geolocation\",\n  \"confidence\": number (0-100),\n  \"suggestions\": [\"suggestion1\", \"suggestion2\", ...],\n  \"locationData\": {\n     \"city\": \"city name\",\n     \"country\": \"country name\",\n     \"coordinates\": {\n        \"latitude\": number,\n        \"longitude\": number\n     },\n     \"landmarks\": [\"landmark1\", ...]\n  }\n}\n\nIMPORTANT: Return ONLY the JSON object, without any markdown or extra text.`;

          const aiResult = await analyzeContent({ image: base64Image, customPrompt: customPrompt });

          // We expect the AI to return JSON, but handle potential errors or non-JSON responses
          if (aiResult && typeof aiResult === 'object' && aiResult !== null && 'explanation' in aiResult) {
             // Map the AI response to our expected geoResult structure
            const formattedResult = {
              gps: gps, // Include original GPS data if available
              aiResult: aiResult // This should contain the parsed JSON from the AI
            };
            setGeoResult(formattedResult);
      setShowGeoModal(true);
          } else {
             // Handle cases where AI didn't return expected JSON
            console.error("AI did not return expected geolocation JSON structure:", aiResult);
            setGeoError('AI analysis failed or returned unexpected format.');
          }
        }
        setGeoLoading(false); // Set loading to false after onload completes
      };
      reader.onerror = (error) => {
        console.error("FileReader Error:", error);
        setGeoError('Failed to read image file.');
    setGeoLoading(false);
      };
      reader.readAsDataURL(geoFile);

    } catch (err: any) {
      console.error("Geolocation Analysis Error:", err);
      setGeoError('Failed to analyze image: ' + (err.message || String(err)));
      setGeoLoading(false);
    }
  };

  const handleImgFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImgError('');
    setImgFile(null);
    setImgPreview(null);
    setExifData(null);
    setManipResult(null);
    setAiGenResult(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setImgFile(file);
    setImgPreview(URL.createObjectURL(file));
  };

  function cleanAIText(text: string) {
    // Remove Markdown bold (**)
    return text.replace(/\*\*/g, '');
  }

  const handleAnalyzeImage = async () => {
    if (!imgFile) return;
    setImgLoading(true);
    setImgError('');
    let analysisPerformed = false;

    try {
      let exifOutput: any = null;
      if (exifChecked && imgFile) {
        try {
          exifOutput = await exifr.parse(imgFile);
          setExifData(exifOutput || { error: "No EXIF data found." });
          analysisPerformed = true;
        } catch (exifError) {
          console.error("EXIF Error:", exifError);
          setExifData({ error: "Failed to parse EXIF data." });
        }
      }

      if (manipChecked && imgFile) {
        try {
          const manipResult = await analyzeImageVerificationAI(imgFile, 'manipulation');
          setManipResult(cleanAIText(manipResult));
        analysisPerformed = true;
        } catch (error: any) {
          console.error("Manipulation Analysis Error:", error);
          setManipResult('Failed to analyze for manipulation.');
        }
      }

      if (aiGenChecked && imgFile) {
        try {
          const aiGenResult = await analyzeImageVerificationAI(imgFile, 'ai-generated');
          setAiGenResult(cleanAIText(aiGenResult));
          analysisPerformed = true;
        } catch (error: any) {
          console.error("AI Generation Analysis Error:", error);
          setAiGenResult('Failed to analyze for AI generation.');
        }
      }

    } catch (error: any) {
      setImgError('Failed to analyze image: ' + error.message);
    } finally {
      setImgLoading(false);
    }
  };

  const handleDocFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDocError('');
    setDocFile(null);
    setDocMeta(null);
    setAiDocResult(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setDocFile(file);
  };

  const handleAnalyzeDocument = async () => {
    if (!docFile) return;
    setDocLoading(true);
    setDocError('');
    setDocMeta(null);
    setAiDocResult(null);
    try {
      if (docFile.type === 'application/pdf') {
        const arrayBuffer = await docFile.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        setDocMeta(pdf?.info || {});
        
        // Extract text from all pages
        let fullText = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map((item: any) => item.str).join(' ');
          fullText += pageText + ' ';
        }
        
        // Analyze the text
        const result = await analyzeDocumentWithAI(fullText);
        setAiDocResult(result);
      } else if (
        docFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        docFile.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ) {
        const arrayBuffer = await docFile.arrayBuffer();
        const zip = await JSZip.loadAsync(arrayBuffer);
        const coreXml = await zip.file('docProps/core.xml')?.async('string');
        setDocMeta(coreXml || 'No metadata found');
        const { value: text } = await mammoth.extractRawText({ arrayBuffer });
      const result = await analyzeDocumentWithAI(text);
        setAiDocResult(result);
      } else {
        setDocError('Unsupported file type. Please upload PDF, DOCX, or PPT.');
      }
    } catch (err) {
      console.error("Document Analysis Error:", err);
      setDocError('Failed to analyze document: ' + (err instanceof Error ? err.message : String(err)));
    }
    setDocLoading(false);
  };

  // Define renderAIResult if not imported
  const renderAIResult = (result: any) => {
    return <pre>{JSON.stringify(result, null, 2)}</pre>;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-4 px-1 sm:px-2 md:px-8 lg:px-16">
      {/* Modal for AI Geolocation Result */}
      {showGeoModal && geoResult && geoResult.aiResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="relative w-w-full max-w-lg mx-auto">
            <Card className="bg-black/90 border border-truth/60 shadow-2xl rounded-2xl">
              <CardHeader>
                <CardTitle className="text-lg text-truth flex items-center gap-2">
                   <FaGlobe className="text-blue-400" /> AI Geolocation Analysis
                </CardTitle>
                <CardDescription className="text-xs text-gray-400">Results from the AI model</CardDescription>
                <button
                  className="absolute top-3 right-3 text-gray-400 hover:text-red-400 text-xl font-bold focus:outline-none"
                  onClick={() => setShowGeoModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </CardHeader>
              <CardContent>
                <div className="space-y-6 text-sm sm:text-base text-gray-200">
                  {/* Display Location Data if available in aiResult.locationData */}
                  {geoResult.aiResult.locationData && (
                    <div className="space-y-4">
                      <div className="font-bold text-truth text-base mb-1">Identified Location</div>
                      <div className="bg-black/40 rounded p-3 border border-gray-700 text-gray-100">
                        {geoResult.aiResult.locationData.city && `City: ${geoResult.aiResult.locationData.city}, `}
                        {geoResult.aiResult.locationData.country && `Country: ${geoResult.aiResult.locationData.country}`}
                        {(!geoResult.aiResult.locationData.city && !geoResult.aiResult.locationData.country) && "Location details not available in AI response."}
                      </div>

                      {geoResult.aiResult.locationData.coordinates && (
                         <div className="bg-black/40 rounded p-3 border border-gray-700 text-gray-100">
                            Coordinates: Latitude {geoResult.aiResult.locationData.coordinates.latitude?.toFixed(6)}, Longitude {geoResult.aiResult.locationData.coordinates.longitude?.toFixed(6)}
                         </div>
                      )}

                      {geoResult.aiResult.locationData.landmarks && geoResult.aiResult.locationData.landmarks.length > 0 && (
                         <div>
                            <div className="font-bold text-truth text-base mb-1">Key Landmarks</div>
                            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                               {geoResult.aiResult.locationData.landmarks.map((landmark: string, i: number) => (
                                  <li key={i}>{landmark}</li>
                               ))}
                            </ul>
                         </div>
                      )}
                    </div>
                  )}

                  {/* Display other analysis details like explanation, confidence, suggestions */}
                  {geoResult.aiResult.explanation && (
                      <div>
                      <div className="font-bold text-truth text-base mb-1">Analysis Explanation</div>
                      <div className="bg-black/40 rounded p-3 border border-gray-700 text-gray-100 whitespace-pre-wrap">{geoResult.aiResult.explanation}</div>
                      </div>
                    )}
                  {geoResult.aiResult.confidence !== undefined && typeof geoResult.aiResult.confidence === 'number' && (
                      <div>
                        <div className="font-bold text-truth text-base mb-1">Confidence Score</div>
                      <div className="bg-black/40 rounded p-3 border border-gray-700 text-gray-100">{(geoResult.aiResult.confidence).toFixed(0)}%</div>
                      </div>
                    )}
                  {geoResult.aiResult.suggestions && geoResult.aiResult.suggestions.length > 0 && (
                      <div>
                        <div className="font-bold text-truth text-base mb-1">Suggestions for Verification</div>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                        {geoResult.aiResult.suggestions.map((s: string, i: number) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                   {/* Source Analysis is less relevant for pure geolocation but keeping it for consistency if AI returns it */}
                   {geoResult.aiResult.sourceAnalysis && (
                      <div>
                        <div className="font-bold text-truth text-base mb-1">Source Analysis</div>
                        <pre className="text-xs bg-black/40 rounded p-3 border border-gray-700 text-gray-100 whitespace-pre-wrap">
                          {JSON.stringify(geoResult.aiResult.sourceAnalysis, null, 2)}
                        </pre>
                  </div>
                  )}

                   {/* Fallback for when locationData is not present but other analysis is */}
                   {!geoResult.aiResult.locationData && (!geoResult.aiResult.explanation && geoResult.aiResult.confidence === undefined && (!geoResult.aiResult.suggestions || geoResult.aiResult.suggestions.length === 0) && !geoResult.aiResult.sourceAnalysis) && (
                      <div className="text-gray-400">No specific location data was identified in the image analysis.</div>
                   )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      <div className="w-full max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl space-y-6 sm:space-y-8 px-1 sm:px-2 md:px-8 lg:px-12">
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-truth">OSINT Tools</h1>
          <p className="mt-2 text-gray-400 text-sm sm:text-base">Advanced open source intelligence tools for investigators</p>
        </div>

        <Tabs key={activeTab} value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {/* Mobile Tabs with Horizontal Slider */}
          <div className="block sm:hidden mb-4">
            {isMobile && (
              <div className="tools-slider">
                <div className="mobile-tabs">
                  {osintCategories.map((category) => (
                    <button
                      key={category.id}
                      className={`mobile-tab ${activeTab === category.id ? 'active' : ''}`}
                      onClick={() => setActiveTab(category.id)}
                    >
                      <div className="flex items-center gap-2">
                        {category.icon}
                        <span>{category.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <TabsList className="hidden">
              {osintCategories.map((category) => (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  className="flex flex-col sm:flex-row items-center justify-center gap-2 px-3 py-2.5 text-xs sm:text-sm rounded-md transition-all duration-200 ease-in-out
                            data-[state=active]:bg-truth data-[state=active]:text-black data-[state=active]:shadow-lg
                            hover:bg-truth/20 focus:relative focus:bg-truth/25"
                >
                  {category.icon}
                  <span className="mt-1 sm:mt-0 text-center sm:text-left leading-tight">{category.name}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Desktop Tabs */}
          <div className="hidden sm:block mb-6">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 bg-gray-800/70 p-1 rounded-lg backdrop-blur-sm">
                  {osintCategories.map((category) => (
                <TabsTrigger
                      key={category.id} 
                      value={category.id}
                  className="flex flex-col sm:flex-row items-center justify-center gap-2 px-3 py-2.5 text-xs sm:text-sm rounded-md transition-all duration-200 ease-in-out
                            data-[state=active]:bg-truth data-[state=active]:text-black data-[state=active]:shadow-lg
                            hover:bg-truth/20 focus:relative focus:bg-truth/25"
                    >
                  {category.icon}
                  <span className="mt-1 sm:mt-0 text-center sm:text-left leading-tight">{category.name}</span>
                </TabsTrigger>
                  ))}
            </TabsList>
          </div>

          {/* Tab Content */}
            <TabsContent value="search" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
              <Card className="bg-black/20 backdrop-blur-sm border-gray-800 rounded-md sm:rounded-lg p-3 sm:p-6">
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg">Domain Intelligence Tool</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                      Analyze website domains for ownership, history, and credibility factors.
                    </CardDescription>
                  </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4">
                  <div>
                        <Input
                          placeholder="Enter domain (e.g., example.com)"
                      className="mb-2 sm:mb-4"
                          value={domain}
                          onChange={e => setDomain(e.target.value)}
                        />
                    <Button className="w-full" onClick={handleDomainAnalyze} disabled={whoisLoading || !domain.trim()}>
                      {whoisLoading ? "Analyzing..." : "Analyze Domain"}
                          </Button>
                    {whoisError && <div className="text-red-400 mt-2">{whoisError}</div>}
                    {whoisResult && (
                      <div className="mt-4 text-xs text-gray-200 max-h-60 overflow-auto bg-black/30 p-2 rounded">
                        <div className="mb-2">
                          <div><b>Domain Name:</b> {whoisResult.WhoisRecord?.domainName || 'N/A'}</div>
                          <div><b>Registrar:</b> {whoisResult.WhoisRecord?.registrarName || 'N/A'}</div>
                          <div><b>Created:</b> {whoisResult.WhoisRecord?.createdDate || 'N/A'}</div>
                          <div><b>Expires:</b> {whoisResult.WhoisRecord?.expiresDate || 'N/A'}</div>
                          <div><b>Updated:</b> {whoisResult.WhoisRecord?.updatedDate || 'N/A'}</div>
                          <div><b>Status:</b> {whoisResult.WhoisRecord?.status || 'N/A'}</div>
                          <div><b>Registrant:</b> {whoisResult.WhoisRecord?.registrant?.organization || whoisResult.WhoisRecord?.registrant?.name || 'N/A'}</div>
                          <div><b>Country:</b> {whoisResult.WhoisRecord?.registrant?.country || 'N/A'}</div>
                          </div>
                        <details>
                          <summary className="cursor-pointer">Show Raw WHOIS JSON</summary>
                          <pre>{JSON.stringify(whoisResult, null, 2)}</pre>
                        </details>
                      </div>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="text-xs text-gray-400 border-t border-gray-800 pt-4">
                  Searches WHOIS data, domain history, and reputation databases.
                </CardFooter>
              </Card>

              <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
                <CardHeader>
                  <CardTitle>Search Operator Builder</CardTitle>
                  <CardDescription>
                    Create advanced search queries for Google, Twitter, and other platforms.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Platform</label>
                      <Select value={searchPlatform} onValueChange={setSearchPlatform}>
                        <SelectTrigger className="w-full bg-black/50 border-gray-600 focus:border-truth">
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 text-white">
                          {platforms.map(p => (
                            <SelectItem key={p.value} value={p.value} className="hover:bg-truth/20 focus:bg-truth/30">
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Search Type</label>
                      <Select value={searchType} onValueChange={setSearchType}>
                        <SelectTrigger className="w-full bg-black/50 border-gray-600 focus:border-truth">
                          <SelectValue placeholder="Select search type" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 text-white">
                          {searchOperators
                            .filter(op => !op.platform || op.platform.includes(searchPlatform))
                            .map(op => (
                            <SelectItem key={op.value} value={op.value} className="hover:bg-truth/20 focus:bg-truth/30">
                              {op.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      </div>
                    </div>
                    <div>
                    <label className="block text-sm font-medium mb-1">Keywords</label>
                      <Input 
                        placeholder="Enter keywords" 
                      value={searchKeywords}
                      onChange={(e) => setSearchKeywords(e.target.value)}
                      className="w-full bg-black/50 border-gray-600 placeholder-gray-500 text-white focus:border-truth"
                      />
                    </div>
                  {searchOperators.find(op => op.value === searchType)?.requiresParameter && (
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {searchOperators.find(op => op.value === searchType)?.parameterLabel || "Parameter"}
                      </label>
                      <Input 
                        type="text" 
                        placeholder={searchOperators.find(op => op.value === searchType)?.parameterPlaceholder || "Enter parameter value"}
                        value={searchType}
                        onChange={(e) => setSearchType(e.target.value)}
                        className="w-full bg-black/50 border-gray-600 placeholder-gray-500 text-white focus:border-truth"
                      />
                      </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-1">Generated Query</label>
                    <Textarea 
                      value={generatedQuery}
                      readOnly
                      placeholder="Your generated query will appear here..."
                      className="w-full min-h-[80px] bg-black/70 border-gray-600 placeholder-gray-500 text-white font-mono text-sm"
                    />
                    </div>
                  <Button onClick={handleCopyQuery} disabled={!generatedQuery} className="w-full">
                    <FaCopy className="mr-2 h-4 w-4" /> Copy Query
                  </Button>
                </CardContent>
              </Card>
            </div>
            {/* Source Analysis History Section */}
            <Card className="bg-black/20 backdrop-blur-sm border-gray-800 rounded-md sm:rounded-lg p-3 sm:p-6 mt-6">
              <CardHeader className="pb-4">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                  <FaFileContract className="h-5 w-5" /> Source Analysis History
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Past domain and source credibility analysis results.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {sourceHistory.length > 0 ? (
                  <div className="space-y-4 max-h-60 overflow-y-auto pr-2">
                    {sourceHistory.map((entry, index) => (
                      <div key={index} className="bg-black/30 p-3 rounded border border-gray-700 text-xs">
                        <p className="font-bold text-truth mb-1">{entry.type.charAt(0).toUpperCase() + entry.type.slice(1)} Analysis: {entry.input}</p>
                        <p className="text-gray-400 mb-2">{new Date(entry.timestamp).toLocaleString()}</p>
                        {/* Basic display of result - can be expanded later */}
                        {entry.type === 'domain' && entry.result?.WhoisRecord?.domainName && (
                          <p>Domain: {entry.result.WhoisRecord.domainName}, Registrar: {entry.result.WhoisRecord.registrarName || 'N/A'}</p>
                        )}
                        {/* Add more history display types as needed */}
                        <details>
                          <summary className="cursor-pointer mt-1">View Full Result (JSON)</summary>
                          <pre className="text-xs mt-2 overflow-x-auto">{JSON.stringify(entry.result, null, 2)}</pre>
                        </details>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-8">
                    No source analysis history yet.
                  </div>
                )}
                {sourceHistory.length > 0 && (
                  <Button variant="outline" size="sm" onClick={handleClearSourceHistory} className="w-full">
                    Clear History
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="network" className="space-y-4">
            <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
              <CardHeader>
                <CardTitle>Network Spread Analyzer</CardTitle>
                <CardDescription>
                  Analyze how information spreads across social networks and identify potential bot activity.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Content URL or Keywords</label>
                    <Input 
                      placeholder="Enter URL or keywords to track"
                      value={networkUrl}
                      onChange={(e) => setNetworkUrl(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Platform</label>
                      <select 
                        className="w-full rounded-md border border-gray-700 bg-black/20 px-3 py-2"
                        value={networkPlatform}
                        onChange={(e) => setNetworkPlatform(e.target.value)}
                      >
                        <option>All Platforms</option>
                        <option>Twitter</option>
                        <option>Facebook</option>
                        <option>Reddit</option>
                        <option>YouTube</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Time Range</label>
                      <select 
                        className="w-full rounded-md border border-gray-700 bg-black/20 px-3 py-2"
                        value={networkTimeRange}
                        onChange={(e) => setNetworkTimeRange(e.target.value)}
                      >
                        <option>Last 24 hours</option>
                        <option>Last 7 days</option>
                        <option>Last 30 days</option>
                        <option>Custom</option>
                      </select>
                    </div>
                  </div>
                  <Button 
                    className="w-full"
                    onClick={handleNetworkAnalysis}
                    disabled={isNetworkLoading}
                  >
                    {isNetworkLoading ? "Analyzing..." : "Analyze Network Spread"}
                  </Button>
                </div>
                <div className="mt-6 p-0 border border-gray-800 rounded-md bg-black/30 h-80">
                  {networkData ? (
                    <NetworkGraph data={networkData} />
                  ) : (
                    <div className="text-center text-gray-400 flex items-center justify-center h-full">
                      <FaNetworkWired className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Network visualization will appear here</p>
                    </div>
                  )}
                </div>
                
                {networkData && (
                  <div className="mt-4">
                    <h3 className="text-lg font-medium mb-2">Analysis Results</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-black/30 border border-gray-800 p-3 rounded-md">
                        <p className="text-sm font-medium text-gray-300 mb-1">Spread Speed</p>
                        <p className="text-2xl font-bold text-truth">High</p>
                        <p className="text-xs text-gray-400">Content is spreading rapidly</p>
                      </div>
                      <div className="bg-black/30 border border-gray-800 p-3 rounded-md">
                        <p className="text-sm font-medium text-gray-300 mb-1">Bot Activity</p>
                        <p className="text-2xl font-bold text-yellow-500">Medium</p>
                        <p className="text-xs text-gray-400">Potential automated accounts detected</p>
                      </div>
                      <div className="bg-black/30 border border-gray-800 p-3 rounded-md">
                        <p className="text-sm font-medium text-gray-300 mb-1">Engagement</p>
                        <p className="text-2xl font-bold text-blue-500">17.3K</p>
                        <p className="text-xs text-gray-400">Total interactions across platforms</p>
                      </div>
                      <div className="bg-black/30 border border-gray-800 p-3 rounded-md">
                        <p className="text-sm font-medium text-gray-300 mb-1">Reach Estimate</p>
                        <p className="text-2xl font-bold text-purple-500">243K</p>
                        <p className="text-xs text-gray-400">Estimated audience exposure</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="geolocation" className="space-y-4">
            <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
              <CardHeader>
                <CardTitle>Geolocation Verification</CardTitle>
                <CardDescription>
                  Verify locations from images and videos using geographical data and landmarks.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-1">Upload Image or Video</label>
                    <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center">
                      {geoPreview ? (
                        <div className="space-y-4">
                          <img 
                            src={geoPreview} 
                            alt="Preview" 
                            className="max-h-60 mx-auto rounded-lg shadow-lg"
                          />
                          <Input type="file" accept="image/*" onChange={handleGeoFileChange} />
                        </div>
                      ) : (
                        <div className="h-60 flex flex-col items-center justify-center">
                      <FaImage className="h-10 w-10 text-gray-500 mb-4" />
                      <p className="text-gray-400 mb-2">Drag and drop or click to upload</p>
                      <Input type="file" accept="image/*" onChange={handleGeoFileChange} />
                        </div>
                      )}
                      <Button 
                        className="w-full mt-4" 
                        onClick={handleVerifyLocation} 
                        disabled={geoLoading || !geoFile}
                      >
                        {geoLoading ? "Analyzing..." : "Verify Location"}
                      </Button>
                      {geoLoading && <p className="text-gray-400 mt-2">Analyzing image...</p>}
                      {geoError && <p className="text-red-400 mt-2">{geoError}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Location Map</label>
                    <div className="border border-gray-700 rounded-lg h-60 bg-black/30 flex items-center justify-center">
                      {geoResult ? (
                        <div className="text-center text-gray-200">
                          <FaGlobe className="h-12 w-12 mx-auto mb-4 text-truth" />
                          <p>Location data will appear here</p>
                        </div>
                      ) : (
                      <div className="text-center text-gray-400">
                        <FaGlobe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Location data will appear here</p>
                      </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="image" className="space-y-4">
            <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
              <CardHeader>
                <CardTitle>Image Verification Suite</CardTitle>
                <CardDescription>
                  Tools to verify images, detect manipulations, and conduct reverse image searches.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Left: Image Upload & Preview */}
                  <div className="flex-1 flex flex-col items-center">
                    <Input type="file" accept="image/*" onChange={handleImgFileChange} />
                    {imgPreview && (
                      <img src={imgPreview} alt="preview" className="max-w-xs my-4 rounded shadow" />
                    )}
                    <div className="flex gap-2 mt-2">
                      <Button variant="secondary" disabled={!imgPreview} onClick={() => window.open(`https://www.google.com/searchbyimage?image_url=${imgPreview}`, '_blank')}>
                        Google Reverse Image
                      </Button>
                      <Button variant="secondary" disabled={!imgPreview} onClick={() => window.open(`https://tineye.com/search?url=${imgPreview}`, '_blank')}>
                        TinEye Reverse Image
                      </Button>
                    </div>
                  </div>

                  {/* Right: Analysis Options & Results */}
                  <div className="flex-1">
                    <div className="mb-4 p-4 bg-black/10 rounded border border-gray-700">
                      <h4 className="font-semibold mb-2">Analysis Options</h4>
                    <div className="space-y-2">
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={exifChecked} onChange={e => setExifChecked(e.target.checked)} />
                          EXIF/Metadata Analysis
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={manipChecked} onChange={e => setManipChecked(e.target.checked)} />
                          Manipulation Analysis
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={aiGenChecked} onChange={e => setAiGenChecked(e.target.checked)} />
                          AI-Generated Content Detection
                        </label>
                      </div>
                      <Button className="w-full mt-4" onClick={handleAnalyzeImage} disabled={imgLoading || !imgFile}>
                        {imgLoading ? "Analyzing..." : "Analyze Image"}
                      </Button>
                      {imgError && <div className="text-red-400 mt-2">{imgError}</div>}
                    </div>
                    {/* Results */}
                    <div className="space-y-4">
                      {exifData && (
                        <div className="p-3 bg-black/20 rounded border border-gray-700">
                          <b>EXIF/Metadata:</b>
                          <pre className="text-xs">{JSON.stringify(exifData, null, 2)}</pre>
                      </div>
                      )}
                      {manipResult && (
                        <Card className="mt-4 bg-black/80 border-2 border-truth/60 shadow-xl rounded-2xl">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg text-truth flex items-center gap-2">
                              <FaCheckCircle className="text-yellow-400" /> Manipulation Analysis
                            </CardTitle>
                            <CardDescription className="text-xs text-gray-400">Results from the AI model</CardDescription>
                          </CardHeader>
                          <CardContent>
                                  <div className="space-y-4">
                                      <div>
                                <div className="font-bold text-truth text-base mb-1">Analysis</div>
                                <p className="text-sm text-gray-300">{manipResult}</p>
                                      </div>
                                      </div>
                          </CardContent>
                        </Card>
                      )}
                      {aiGenResult && (
                        <Card className="mt-4 bg-black/80 border-2 border-truth/60 shadow-xl rounded-2xl">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg text-truth flex items-center gap-2">
                              <FaCheckCircle className="text-green-400" /> AI Image Analysis
                            </CardTitle>
                            <CardDescription className="text-xs text-gray-400">Results from the AI model</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                                      <div>
                                <div className="font-bold text-truth text-base mb-1">Analysis</div>
                                <p className="text-sm text-gray-300">{aiGenResult}</p>
                                      </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <Button className="w-full">Analyze Image</Button>
                </div>
                <div className="mt-6 p-4 border border-gray-800 rounded-md bg-black/30">
                  <h3 className="font-medium mb-2">Results will appear here</h3>
                  <p className="text-sm text-gray-400">
                    The analysis will check for source, modifications, metadata, and other forensic indicators.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="document" className="space-y-4">
            <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
              <CardHeader>
                <CardTitle>AI Content Analysis</CardTitle>
                <CardDescription>
                  Use AI to analyze text for potential misinformation, bias, or manipulation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Enter Text to Analyze</label>
                  <Textarea
                    placeholder="Paste text to analyze for potential misinformation or manipulation..."
                    rows={6}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    The AI will analyze the content for potential misinformation, bias, or manipulative language.
                  </p>
                </div>
                <Button
                  className="w-full"
                  onClick={handleTextAnalysis}
                  disabled={isAnalyzing || !inputText.trim()}
                >
                  {isAnalyzing ? "Analyzing..." : "Analyze Text"}
                </Button>
               {analyzeResult && (
                 <Card className="mt-4 bg-black/80 border-2 border-truth/60 shadow-xl rounded-2xl">
                   <CardHeader className="pb-2">
                     <CardTitle className="text-lg text-truth flex items-center gap-2">
                       <FaFileContract className="text-blue-400" /> Text Analysis Result
                     </CardTitle>
                     <CardDescription className="text-xs text-gray-400">AI insights from the provided text</CardDescription>
                   </CardHeader>
                   <CardContent>
                     <div className="space-y-4 text-sm sm:text-base text-gray-200">
                       {/* Score and Brief Explanation */}
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         {/* Score */}
                         {typeof analyzeResult.confidence === 'number' && (
                           <div className="bg-black/40 rounded-lg p-4 border border-gray-700">
                             <div className="font-bold text-truth text-base mb-2 flex items-center gap-2">
                               <FaChartLine className="text-yellow-400" /> Credibility Score
                  </div>
                             <div className="flex items-center gap-3">
                               <div className="text-3xl font-bold text-truth">
                                 {analyzeResult.confidence.toFixed(0)}%
                               </div>
                               <div className="w-full bg-gray-700 rounded-full h-2.5">
                                 <div 
                                   className="bg-truth h-2.5 rounded-full" 
                                   style={{ width: `${analyzeResult.confidence}%` }}
                                 ></div>
                               </div>
                             </div>
                         </div>
                       )}

                         {/* Brief Explanation */}
                         {analyzeResult.explanation && (
                           <div className="bg-black/40 rounded-lg p-4 border border-gray-700">
                             <div className="font-bold text-truth text-base mb-2 flex items-center gap-2">
                               <FaFileAlt className="text-blue-400" /> Analysis
                             </div>
                             <div className="text-gray-100">
                               {analyzeResult.explanation.split('\n')[0]} {/* Only show first paragraph */}
                             </div>
                         </div>
                       )}
                       </div>

                       {/* Quick Recommendations */}
                       {analyzeResult.suggestions && analyzeResult.suggestions.length > 0 && (
                         <div className="bg-black/40 rounded-lg p-4 border border-gray-700">
                           <div className="font-bold text-truth text-base mb-2 flex items-center gap-2">
                             <FaLightbulb className="text-yellow-400" /> Quick Tips
                          </div>
                           <ul className="space-y-2">
                             {analyzeResult.suggestions.slice(0, 2).map((suggestion, index) => ( // Only show first 2 suggestions
                               <li key={index} className="flex items-start gap-2">
                                 <span className="text-truth mt-1">•</span>
                                 <span className="text-gray-100">{suggestion}</span>
                                       </li>
                                     ))}
                                   </ul>
                               </div>
                       )}
                     </div>
                   </CardContent>
                 </Card>
                )}
              </CardContent>
            </Card>
            
            <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
              <CardHeader>
                <CardTitle>Document Forensics</CardTitle>
                <CardDescription>
                  Analyze PDFs and documents for authenticity and metadata.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Upload Document</label>
                  <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center flex flex-col items-center justify-center">
                    <FaFileAlt className="h-10 w-10 text-gray-500 mb-4" />
                    <p className="text-gray-400 mb-2">Drag and drop or click to upload</p>
                    <p className="text-xs text-gray-500 mb-4">Supports PDF, DOCX, PPT files</p>
                    <Input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx" onChange={handleDocFileChange} />
                    <Button className="w-full mt-2" onClick={handleAnalyzeDocument} disabled={docLoading || !docFile}>
                      {docLoading ? "Analyzing..." : "Analyze Document"}
                    </Button>
                    {docError && <div className="text-red-400 mt-2">{docError}</div>}
                    {docMeta && (
                      <div className="p-3 bg-black/20 rounded border border-gray-700 mt-2">
                        <b>Document Metadata:</b>
                        <pre className="text-xs">{typeof docMeta === 'string' ? docMeta : JSON.stringify(docMeta, null, 2)}</pre>
                      </div>
                    )}
                    {aiDocResult && (
                      <Card className="mt-4 bg-black/80 border-2 border-truth/60 shadow-xl rounded-2xl">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg text-truth flex items-center gap-2">
                            <FaCheckCircle className="text-green-400" /> Document Analysis
                          </CardTitle>
                          <CardDescription className="text-xs text-gray-400">Results from the AI model</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                                    <div>
                              <div className="font-bold text-truth text-base mb-1">Analysis</div>
                              <p className="text-sm text-gray-300">{aiDocResult}</p>
                                    </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>

              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="py-6">
          <div className="bg-black/30 backdrop-blur-sm rounded-lg border border-gray-800 p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="bg-truth/20 rounded-full p-4">
                <FaTools className="h-8 w-8 text-truth" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold mb-2">Need More Advanced Tools?</h2>
                <p className="text-gray-400 mb-4">
                  For professional investigators, journalists, and researchers, we offer advanced OSINT tools with
                  deeper analysis capabilities, cross-platform searches, and automated monitoring.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button variant="default">Explore Pro Tools</Button>
                  <Button variant="outline">Contact Us</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tools;
